import { useState } from "react"
import { router, usePage } from "@inertiajs/react"
import { Check } from "lucide-react"
import { useIntl } from "react-intl"
import { ACCENTS, DEFAULT_ACCENT, applyAccent, type AccentId } from "@/lib/accents"
import { cn } from "@/lib/utils"
import type { PageProps } from "@/types/inertia"

/** Swatches for choosing the app's accent color. The change shows at once and is saved to the account. */
export function AccentPicker() {
  const intl = useIntl()
  const saved = (usePage<PageProps>().props.current_user?.accent ?? DEFAULT_ACCENT) as AccentId
  const [selected, setSelected] = useState<AccentId>(saved)

  const choose = (id: AccentId) => {
    if (id === selected) return
    setSelected(id)
    applyAccent(id)
    router.patch(
      "/settings/appearance",
      { accent: id === DEFAULT_ACCENT ? "" : id },
      {
        preserveScroll: true,
        preserveState: true,
        onError: () => {
          setSelected(saved)
          applyAccent(saved)
        },
      },
    )
  }

  return (
    <div role="radiogroup" aria-label={intl.formatMessage({ id: "settings.accent_label" })} className="flex flex-wrap gap-3">
      {ACCENTS.map((accent) => {
        const active = selected === accent.id
        return (
          <button
            key={accent.id}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={intl.formatMessage(accent.label)}
            title={intl.formatMessage(accent.label)}
            onClick={() => choose(accent.id)}
            className={cn(
              "flex h-10 w-10 cursor-pointer items-center justify-center rounded-full shadow-[inset_0_0_0_1px_rgb(128_128_128/0.35)] ring-offset-2 ring-offset-page transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-display",
              active && "ring-2 ring-ink-display",
            )}
            style={{ backgroundColor: accent.swatch }}
          >
            {active && <Check className="h-4 w-4 text-white" aria-hidden="true" />}
          </button>
        )
      })}
    </div>
  )
}
